import { queryOptions, useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/core/auth/hooks/useAuth";
import { useCurrentBusiness } from "@/features/business/hooks/useCurrentBusiness";
import { calendarEventApiService } from "../services/calendarEventApiService";
import {
  fromCalendarEventDtoToCalendarEvent,
  fromCalendarEventConflictRequestToDto,
  fromCalendarEventConflictPreviewDtoToPreview,
  fromCreateCalendarEventRequestToCreateCalendarEventDto,
  fromUpdateCalendarEventRequestToUpdateCalendarEventDto,
  type CalendarEvent,
  type CalendarEventConflictRequest,
  type CreateCalendarEventRequest,
  type UpdateCalendarEventRequest,
} from "../types/calendarEventTypes";

export const calendarEventKeys = {
  all: ["calendarEvents"] as const,
  business: (businessId: string | undefined, userId?: string) => ["calendarEvents", businessId, userId] as const,
  range: (businessId: string | undefined, userId: string | undefined, startAt: Date, endAt: Date) =>
    [...calendarEventKeys.business(businessId, userId), "range", startAt.toISOString(), endAt.toISOString()] as const,
  detail: (businessId: string | undefined, userId: string | undefined, id: string) =>
    [...calendarEventKeys.business(businessId, userId), "detail", id] as const,
};

function calendarEventsQueryOptions(businessId: string | undefined, userId: string | undefined, startAt: Date, endAt: Date, enabled = true) {
  return queryOptions({
    queryKey: calendarEventKeys.range(businessId, userId, startAt, endAt),
    queryFn: async ({ signal }) => {
      if (!businessId) throw new Error("Choose a business first.");
      const dtos = await calendarEventApiService.getAll(businessId, startAt.toISOString(), endAt.toISOString(), signal);
      signal.throwIfAborted();
      return dtos.map(fromCalendarEventDtoToCalendarEvent).filter((event) => event.businessId === businessId);
    },
    enabled: enabled && !!businessId && !!userId,
    staleTime: 60_000,
  });
}

export function useCalendarEvents(startAt: Date, endAt: Date, enabled = true) {
  const { businessId } = useCurrentBusiness();
  const { user } = useAuth();
  return useQuery(calendarEventsQueryOptions(businessId, user?.id, startAt, endAt, enabled));
}

export function useCalendarEventsForRanges(ranges: { startAt: Date; endAt: Date }[]) {
  const { businessId } = useCurrentBusiness();
  const { user } = useAuth();
  const queries = useQueries({
    queries: ranges.map((range) => calendarEventsQueryOptions(businessId, user?.id, range.startAt, range.endAt)),
  });
  return {
    data: queries.map((query) => query.data),
    error: queries.find((query) => query.error)?.error ?? null,
    isLoading: queries.some((query) => query.isLoading),
    isFetching: queries.some((query) => query.isFetching),
    refetch: () => Promise.all(queries.map((query) => query.refetch())),
  };
}

export function useCalendarEvent(id: string) {
  const { businessId } = useCurrentBusiness();
  const { user } = useAuth();
  return useQuery({
    queryKey: calendarEventKeys.detail(businessId, user?.id, id),
    queryFn: async ({ signal }) => {
      if (!businessId) throw new Error("Choose a business first.");
      const event = fromCalendarEventDtoToCalendarEvent(await calendarEventApiService.getById(businessId, id, signal));
      signal.throwIfAborted();
      if (event.businessId !== businessId) throw new Error("Event not found in this business.");
      return event;
    },
    enabled: !!id && !!businessId && !!user,
    staleTime: 0,
  });
}

function useCalendarEventScope() {
  const { businessId } = useCurrentBusiness();
  const { user, getAuthGeneration } = useAuth();
  const queryClient = useQueryClient();
  function capture() {
    if (!businessId || !user) throw new Error("Choose a business and sign in before changing events.");
    const generation = getAuthGeneration();
    return {
      businessId, userId: user.id,
      check() {
        if (generation !== getAuthGeneration()) throw new Error("Your account changed. Reopen this event.");
      },
    };
  }
  async function invalidate(scope: ReturnType<typeof capture>, event?: CalendarEvent, deletedId?: string) {
    scope.check();
    if (event) {
      if (event.businessId !== scope.businessId) throw new Error("Event not found in this business.");
      queryClient.setQueryData(calendarEventKeys.detail(scope.businessId, scope.userId, event.id), event);
    }
    if (deletedId) queryClient.removeQueries({ queryKey: calendarEventKeys.detail(scope.businessId, scope.userId, deletedId) });
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: calendarEventKeys.business(scope.businessId, scope.userId) }),
      queryClient.invalidateQueries({ queryKey: ["availableTimeSlots", { businessId: scope.businessId, userId: scope.userId }] }),
    ]);
    scope.check();
  }
  return { capture, invalidate };
}

export function useCreateCalendarEvent() {
  const { capture, invalidate } = useCalendarEventScope();
  return useMutation({
    meta: { action: "calendar_event.create" },
    mutationFn: async (data: CreateCalendarEventRequest) => {
      const scope = capture();
      if (data.businessId !== scope.businessId) throw new Error("The selected business changed. Reopen this form.");
      const event = fromCalendarEventDtoToCalendarEvent(await calendarEventApiService.create(scope.businessId, fromCreateCalendarEventRequestToCreateCalendarEventDto(data)));
      await invalidate(scope, event);
      return event;
    },
  });
}

export function useUpdateCalendarEvent(id: string) {
  const { capture, invalidate } = useCalendarEventScope();
  return useMutation({
    meta: { action: "calendar_event.update" },
    mutationFn: async (data: UpdateCalendarEventRequest) => {
      const scope = capture();
      const event = fromCalendarEventDtoToCalendarEvent(await calendarEventApiService.update(scope.businessId, id, fromUpdateCalendarEventRequestToUpdateCalendarEventDto(data)));
      await invalidate(scope, event);
      return event;
    },
  });
}

export function useDeleteCalendarEvent(id: string) {
  const { capture, invalidate } = useCalendarEventScope();
  return useMutation({
    meta: { action: "calendar_event.delete" },
    mutationFn: async (expectedVersion: number) => {
      const scope = capture();
      await calendarEventApiService.delete(scope.businessId, id, expectedVersion);
      await invalidate(scope, undefined, id);
    },
  });
}

export function useCalendarEventConflicts() {
  const { capture } = useCalendarEventScope();
  return useMutation({
    mutationFn: async (data: CalendarEventConflictRequest) => {
      const scope = capture();
      const preview = await calendarEventApiService.getConflicts(scope.businessId, fromCalendarEventConflictRequestToDto(data));
      scope.check();
      return fromCalendarEventConflictPreviewDtoToPreview(preview);
    },
  });
}
